import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import useReveal from "@/hooks/useReveal";

const LAST_UPDATED = "21 June 2026";
const EFFECTIVE_DATE = "21 June 2026";

/* ---------- small reusable bits ---------- */

const A = ({ href, children }) => (
  <a
    href={href}
    className="underline decoration-[var(--leo-orange)] decoration-2 underline-offset-4 hover:text-[var(--leo-orange-deep)]"
  >
    {children}
  </a>
);

const Em = ({ children }) => (
  <strong className="text-[var(--leo-ink)]">{children}</strong>
);

const SubHeading = ({ children }) => (
  <h3 className="font-display text-lg sm:text-xl text-[var(--leo-ink)] mt-6 mb-2 first:mt-0">
    {children}
  </h3>
);

/* ---------- terms sections ---------- */

const accents = [
  "#FFE1C4",
  "#DDE8F8",
  "#D6EEDF",
  "#FFF0CC",
  "#FBE6C8",
];

const sections = [
  {
    n: "01",
    title: "About these terms",
    body: (
      <>
        <p>
          These Terms of Service ("Terms") are a legal agreement between you (the
          parent or legal guardian, "you") and Code Ninja Coaching Ltd ("we",
          "us", "our") governing your use of Meet Leo (the "Service"), available
          at meetleo.co.uk and via the Meet Leo iOS application.
        </p>
        <p className="mt-3">
          By creating an account, subscribing, or otherwise using the Service,
          you confirm that you have read, understood, and agree to these Terms.
          If you do not agree, please do not use the Service.
        </p>
        <p className="mt-3">
          We are registered in England and Wales (Code Ninja Coaching Ltd).
          These Terms work alongside our{" "}
          <Link
            to="/privacy"
            className="underline decoration-[var(--leo-orange)] decoration-2 underline-offset-4 hover:text-[var(--leo-orange-deep)]"
          >
            Privacy Policy
          </Link>
          , which explains how we handle personal data.
        </p>
      </>
    ),
  },
  {
    n: "02",
    title: "Who can use Meet Leo",
    body: (
      <>
        <p>
          Meet Leo is a voice-based AI maths companion designed for children in
          UK primary school (Years 1–6, ages 5–11).
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            <Em>You must be 18 or over</Em> and the parent or legal guardian of
            the child who will use the Service.
          </li>
          <li>
            <Em>Only a parent or guardian</Em> may create the account, provide
            consent, manage the subscription, and control the Service.
          </li>
          <li>
            <Em>Children do not create accounts</Em> and cannot use the Service
            without a parent or guardian setting it up and being present.
          </li>
        </ul>
        <p className="mt-3">
          By setting up a child profile, you confirm that you hold parental
          responsibility for that child and consent to the processing described
          in our Privacy Policy.
        </p>
      </>
    ),
  },
  {
    n: "03",
    title: "Your account",
    body: (
      <>
        <p>
          You create your account using Google Sign-In. You are responsible for
          keeping access to your account secure.
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            Keep your sign-in credentials and device secure, and do not share
            them with anyone who should not have access to the Service.
          </li>
          <li>
            Every voice session begins with a parent biometric unlock (FaceID,
            TouchID, or device passcode). This is by design and cannot be
            switched off. You agree to be present at the start of each session.
          </li>
          <li>
            You are responsible for the activity that takes place under your
            account, including your child's use of the Service while you are
            supervising.
          </li>
          <li>
            Please give us accurate information when setting up your account and
            child profile, and keep it up to date.
          </li>
        </ul>
      </>
    ),
  },
  {
    n: "04",
    title: "Subscriptions, billing, and trials",
    body: (
      <>
        <SubHeading>4.1 How billing works</SubHeading>
        <p>
          Meet Leo is offered on a subscription basis. Subscriptions are
          purchased and managed through Apple's in-app purchase system. Apple
          handles all payments — <Em>we never see or store your card details</Em>.
        </p>
        <SubHeading>4.2 Renewal</SubHeading>
        <p>
          Subscriptions renew automatically at the end of each billing period
          unless cancelled at least 24 hours before the end of the current
          period. Your Apple ID account is charged on renewal at the price shown
          at the time of purchase.
        </p>
        <SubHeading>4.3 Free trials</SubHeading>
        <p>
          Where a free trial is offered, it converts to a paid subscription
          automatically at the end of the trial unless you cancel before it ends.
          Any unused portion of a free trial is forfeited when you purchase a
          subscription.
        </p>
        <SubHeading>4.4 Managing and cancelling</SubHeading>
        <p>
          You can manage or cancel your subscription at any time in your Apple ID
          account settings on your device. Cancellation takes effect at the end
          of the current billing period; you retain access until then.
        </p>
        <SubHeading>4.5 Refunds</SubHeading>
        <p>
          Refunds for App Store purchases are handled by Apple in line with
          Apple's refund policy. If you believe you are entitled to a refund,
          please contact Apple Support. We are happy to help where we can — email{" "}
          <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A>.
        </p>
        <SubHeading>4.6 Price changes</SubHeading>
        <p>
          We may change subscription prices from time to time. Where required, we
          will give you advance notice and the opportunity to cancel before a new
          price takes effect.
        </p>
      </>
    ),
  },
  {
    n: "05",
    title: "Acceptable use",
    body: (
      <>
        <p>When using Meet Leo, you agree that you will not:</p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            Use the Service for any unlawful purpose or in breach of these Terms.
          </li>
          <li>
            Attempt to access another person's account or data, or circumvent the
            parent biometric unlock or any other security measure.
          </li>
          <li>
            Reverse engineer, decompile, copy, resell, or commercially exploit
            the Service or any part of it, except where the law permits.
          </li>
          <li>
            Interfere with, disrupt, or place an unreasonable load on the Service
            or its infrastructure, or attempt to introduce malicious code.
          </li>
          <li>
            Use the Service to harm a child, or knowingly allow a child to use the
            Service in a way that is unsafe or unsupervised.
          </li>
          <li>
            Misuse the safeguarding features, or rely on the Service as a
            substitute for appropriate adult supervision or professional support.
          </li>
        </ul>
        <p className="mt-3">
          We may suspend or terminate access where we reasonably believe these
          Terms have been breached or the Service is being misused.
        </p>
      </>
    ),
  },
  {
    n: "06",
    title: "The AI companion — what to expect",
    body: (
      <>
        <p>
          Leo is powered by artificial intelligence. While we work hard to make
          Leo helpful, supportive, and age-appropriate, AI systems are not
          perfect.
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            Leo's responses may occasionally be inaccurate, incomplete, or not
            suited to every child. Leo is a companion to support maths practice —
            not a replacement for a teacher, tutor, or the school curriculum.
          </li>
          <li>
            Leo is not a medical, psychological, or professional advice service.
            If your child needs support beyond maths practice, please seek help
            from a qualified professional.
          </li>
          <li>
            The Service includes a safeguarding layer designed to identify if a
            child may be at risk (see our Privacy Policy, Section 9). This is a
            safety feature, not a guarantee, and does not replace your role as a
            parent or carer.
          </li>
          <li>
            You remain responsible for supervising your child's use of the
            Service and for reviewing the conversations available to you in the
            parent dashboard.
          </li>
        </ul>
      </>
    ),
  },
  {
    n: "07",
    title: "Intellectual property",
    body: (
      <>
        <p>
          The Service, including Leo's character, the app, its content, design,
          and software, is owned by Code Ninja Coaching Ltd and its licensors and
          is protected by intellectual property laws.
        </p>
        <p className="mt-3">
          We grant you a limited, personal, non-exclusive, non-transferable,
          revocable licence to use the Service for your and your child's personal,
          non-commercial use, in accordance with these Terms. You may not copy,
          modify, distribute, sell, or lease any part of the Service.
        </p>
        <p className="mt-3">
          Any feedback or suggestions you choose to share with us may be used to
          improve the Service without obligation to you.
        </p>
      </>
    ),
  },
  {
    n: "08",
    title: "Privacy and your child's data",
    body: (
      <>
        <p>
          Your privacy, and your child's, matters to us. How we collect, use,
          store, and protect personal data is set out in full in our{" "}
          <Link
            to="/privacy"
            className="underline decoration-[var(--leo-orange)] decoration-2 underline-offset-4 hover:text-[var(--leo-orange-deep)]"
          >
            Privacy Policy
          </Link>
          , which forms part of these Terms.
        </p>
        <p className="mt-3">
          In short: <Em>we do not sell personal data</Em>, we do not show
          children adverts, and we do not allow our AI providers to train their
          models on your or your child's data. By using the Service, you
          acknowledge the data practices described in the Privacy Policy.
        </p>
      </>
    ),
  },
  {
    n: "09",
    title: "Availability and changes to the Service",
    body: (
      <>
        <p>
          We aim to keep the Service running smoothly, but we cannot guarantee it
          will always be available, uninterrupted, or error-free. The Service is
          provided on an "as available" basis.
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            We may carry out maintenance, updates, or improvements that
            temporarily affect availability.
          </li>
          <li>
            We may add, change, or remove features over time. Where a change
            materially reduces the core Service, we will give reasonable notice
            where we can.
          </li>
          <li>
            The Service relies on third-party providers (for example, hosting,
            speech-to-text, and AI providers). Interruptions to those services
            may affect Meet Leo.
          </li>
        </ul>
      </>
    ),
  },
  {
    n: "10",
    title: "Liability",
    body: (
      <>
        <p>
          Nothing in these Terms limits or excludes our liability for death or
          personal injury caused by our negligence, for fraud or fraudulent
          misrepresentation, or for any other liability that cannot be limited or
          excluded under the laws of England and Wales.
        </p>
        <p className="mt-3">
          Subject to the above, and to the fullest extent permitted by law:
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            The Service is provided for the personal, non-commercial use of you
            and your child, and we are not liable for any business losses.
          </li>
          <li>
            We are not liable for loss or damage that is not reasonably
            foreseeable, or that arises from your failure to supervise your
            child's use of the Service.
          </li>
          <li>
            Where we are found liable to you, our total liability arising out of
            or in connection with the Service is limited to the amount you paid us
            for the Service in the 12 months before the event giving rise to the
            liability.
          </li>
        </ul>
        <p className="mt-3">
          This does not affect your statutory rights as a consumer, which remain
          unaffected.
        </p>
      </>
    ),
  },
  {
    n: "11",
    title: "Suspension and termination",
    body: (
      <>
        <p>
          You can stop using the Service and delete your account at any time. On
          account deletion, we delete the personal data we hold as described in
          the Privacy Policy, except where we are required by law to retain it
          (for example, safeguarding and limited billing records).
        </p>
        <p className="mt-3">
          We may suspend or terminate your access to the Service if you breach
          these Terms, if we are required to by law, or if continuing to provide
          the Service is no longer reasonably possible. Where appropriate and
          lawful, we will give you notice.
        </p>
        <p className="mt-3">
          If we discontinue the Service entirely, we will give reasonable notice
          and, where relevant, handle any remaining paid subscription period
          fairly.
        </p>
      </>
    ),
  },
  {
    n: "12",
    title: "Changes to these Terms",
    body: (
      <>
        <p>
          We may update these Terms from time to time. Material changes will be
          notified to account holders by email at least 14 days before they take
          effect.
        </p>
        <p className="mt-3">
          Continued use of the Service after a change takes effect indicates
          acceptance of the updated Terms. If you do not accept a change, you may
          delete your account at any time. The "Last updated" date at the top of
          these Terms reflects the most recent change.
        </p>
      </>
    ),
  },
  {
    n: "13",
    title: "Governing law and disputes",
    body: (
      <>
        <p>
          These Terms, and any dispute arising out of or in connection with them
          or the Service, are governed by the laws of England and Wales.
        </p>
        <p className="mt-3">
          The courts of England and Wales have non-exclusive jurisdiction,
          although if you live elsewhere in the UK you may bring proceedings in
          your local courts. If you have a concern, please contact us first at{" "}
          <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A> — we
          will always try to resolve things directly.
        </p>
      </>
    ),
  },
  {
    n: "14",
    title: "Contact",
    body: (
      <ul className="space-y-2">
        <li>
          <span className="font-display text-[var(--leo-ink)]">Provider:</span>{" "}
          Code Ninja Coaching Ltd (England and Wales)
        </li>
        <li>
          <span className="font-display text-[var(--leo-ink)]">Email:</span>{" "}
          <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A>
        </li>
        <li>
          <span className="font-display text-[var(--leo-ink)]">Website:</span>{" "}
          meetleo.co.uk
        </li>
      </ul>
    ),
  },
];

export default function Terms() {
  useReveal();

  return (
    <main
      data-testid="terms-page"
      className="site min-h-screen pt-[72px]"
    >
      <Nav links={false} />

      {/* Hero */}
      <section className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-10 text-center">
        <span className="eyebrow eyebrow--plain mb-5">Terms of Service · Meet Leo</span>
        <h1
          data-testid="terms-headline"
          className="font-display text-[2.2rem] sm:text-5xl lg:text-6xl leading-[1.05] text-[var(--leo-ink)]"
        >
          The <span className="said">fair</span> bit.
        </h1>
        <p className="mt-6 font-body text-lg sm:text-xl text-[var(--leo-ink-soft)] leading-relaxed">
          Operated by Code Ninja Coaching Ltd. These are the terms for using Meet
          Leo — written to be readable. Here's what you can expect from us, and
          what we ask of you.
        </p>
        <p className="mt-4 font-body text-sm text-[var(--leo-ink-soft)]">
          Last updated: {LAST_UPDATED} · Effective date: {EFFECTIVE_DATE}
        </p>
      </section>

      {/* Sections */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 space-y-8 sm:space-y-10">
        {sections.map((s, i) => (
          <article
            key={s.n}
            className="reveal sticker-soft bg-[var(--leo-paper)] p-6 sm:p-8 relative"
          >
            <div className="flex items-start gap-4 sm:gap-5">
              <div
                aria-hidden
                className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center font-display text-base sm:text-lg text-[var(--leo-ink)]"
                style={{ background: accents[i % accents.length] }}
              >
                {s.n}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-2xl sm:text-3xl text-[var(--leo-ink)] leading-tight">
                  {s.title}
                </h2>
                <div className="mt-4 font-body text-base sm:text-lg text-[var(--leo-ink-soft)] leading-relaxed">
                  {s.body}
                </div>
              </div>
            </div>
          </article>
        ))}

        {/* Contact CTA */}
        <article className="reveal sticker bg-[var(--leo-cream-deep)] p-6 sm:p-8 text-center">
          <p className="font-hand text-3xl text-[var(--leo-ink)] mb-2">
            questions?
          </p>
          <h2 className="font-display text-2xl sm:text-3xl text-[var(--leo-ink)] leading-tight">
            We're real humans. Just ask.
          </h2>
          <p className="mt-4 font-body text-base sm:text-lg text-[var(--leo-ink-soft)] leading-relaxed">
            Not sure about something in these terms? Drop us a line and we'll
            explain it plainly.
          </p>
          <a
            href="mailto:founder@meetleo.co.uk"
            data-testid="terms-contact-email"
            className="btn btn--orange mt-6"
          >
            founder@meetleo.co.uk
            <span aria-hidden>→</span>
          </a>
          <p className="mt-6 font-body text-xs text-[var(--leo-ink-soft)]">
            These Terms are governed by the laws of England and Wales.
          </p>
        </article>
      </section>

      <Footer />
    </main>
  );
}
